import React, { useCallback } from 'react';

import { ModalTransition } from '@atlaskit/modal-dialog';
import { SEARCH_3P_CONFIG } from '@atlassian/search-client';
import { removeLocalStorageItem } from '@atlassian/search-common';

import { NEWLY_CONNECTED_PRODUCTS_EXPIRY_MS } from '../../../common/constants/3p-bulk-connect';
import { ProductKeys3P } from '../../../common/constants/3p-product-configs';
import { is3pProductKey } from '../../../common/constants/products';
import { useAppContext } from '../../../common/ui/app-context';
import { BulkConnectDialog } from '../../../common/ui/bulk-connect-dialog';
import type { OAuthSource } from '../../../common/utils/events/oauth-events';
import { useBulkConnectLocalStorage } from '../../../common/utils/use-bulk-connect-local-storage';
import {
	useBulkConnectModalOpenState,
	useBulkConnectState,
	useInitializeBulkConnect,
	useNewlyConnectedProducts,
} from '../../../controllers/bulk-connect';
import { useOAuthHandlers } from '../../../controllers/oauth';
import { useBootstrap } from '../../../controllers/store/bootstrap';
import { useIsBulkConnectModalOpen as useIsBulkConnectModalOpenQuickFind } from '../../../controllers/store/quick-find';

export const BulkConnectModal = ({
	isOnSearchDialog,
	oAuthSource,
}: {
	isOnSearchDialog?: boolean;
	oAuthSource: OAuthSource;
}): React.JSX.Element => {
	const [{ thirdPartyConfigs, products: availableProducts }] = useBootstrap();
	const { generateSearchUrl, queryParams } = useAppContext();
	const { getNewlyConnectedProducts } = useNewlyConnectedProducts();
	const newlyConnectedProducts = getNewlyConnectedProducts(NEWLY_CONNECTED_PRODUCTS_EXPIRY_MS);
	const availableProductsAs3P = availableProducts.filter(
		(productKey): productKey is ProductKeys3P =>
			is3pProductKey(productKey) && productKey !== ProductKeys3P.AtlassianWebCrawler,
	);
	useInitializeBulkConnect(availableProductsAs3P, newlyConnectedProducts);

	const [storedState, setState] = useBulkConnectLocalStorage();
	const quickFindState = useIsBulkConnectModalOpenQuickFind();
	const fullPageState = useBulkConnectModalOpenState();

	const [isBulkConnectModalOpen, { setIsBulkConnectModalOpen }] = isOnSearchDialog
		? quickFindState
		: [
				fullPageState.isBulkConnectModalOpen,
				{ setIsBulkConnectModalOpen: fullPageState.setIsBulkConnectModalOpen },
			];

	const onCloseModal = useCallback(
		(wasConnected: boolean, closedByButton: boolean) => {
			if (wasConnected && closedByButton) {
				setState({
					...storedState,
					bulkConnectBanner: {
						initialBannerDismissed: true,
						secondaryBannerDismissed: true,
					},
				});
			}
			// We clear 3p config cache and refresh to synchronise the state with FPS
			// Details: https://hello.atlassian.net/wiki/spaces/CASE/pages/4670854655/3P+Discoverability+Bug
			if (wasConnected && isOnSearchDialog) {
				removeLocalStorageItem(SEARCH_3P_CONFIG);
				const searchUrl = generateSearchUrl(queryParams);
				window.location.href = searchUrl;
			}

			setIsBulkConnectModalOpen(false);
		},
		[
			generateSearchUrl,
			isOnSearchDialog,
			queryParams,
			setIsBulkConnectModalOpen,
			setState,
			storedState,
		],
	);

	return (
		<ModalTransition>
			{isBulkConnectModalOpen && (
				<BulkConnectDialog
					newlyConnected={newlyConnectedProducts}
					onClose={onCloseModal}
					thirdPartyConfigs={thirdPartyConfigs}
					useOAuthHandlers={useOAuthHandlers}
					useBulkConnectState={useBulkConnectState}
					oAuthSource={oAuthSource}
				/>
			)}
		</ModalTransition>
	);
};
